import { Spacer } from '@chakra-ui/react';
import { Image, Loader } from '@mantine/core';
import { Divider } from '@mui/material';
import image from 'assets/images/home-images/img-2.png';
import LOCALSTORAGE_KEYS from 'constants/localStorage';
import renderIcon from 'icon';
import QUERY_KEYS from 'network/config/queryKeys';
import { clearCart, deleteOrder } from 'network/services/goods';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { BiTrash } from 'react-icons/bi';
import { IoArrowBackCircle } from 'react-icons/io5';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userIsLoggedIn, userRoleAtom } from 'store/atoms/userAtom';
import { formatCurrency } from 'utils/currencyFormater';
import { CartIcon } from '../../../assets/Icons';
import { CHECKOUT } from '../../../navigation/routes';
import { isCartOpen } from '../../../store/atoms/cartAtom';
import { EmptyCon } from '../../atoms';
import { Modal } from '../Modal/Modal';
import { SideModalContentStyle, SideModalStyle } from '../Modal/style';
import CartOrders from './CartOrders';
import { CartProduct } from './CartProduct';
import { NewUseUpdatedCart, useCartInvalidation } from './hooks';
import { CartCon, CheckoutBottom, SubTotal } from './style';

const initial = {
  x: '100%',
  height: 'auto'
};
const animate = {
  x: '0'
};

export const Cart = () => {
  const setIsModalOpen = useSetRecoilState(isCartOpen);
  const isModalOpen = useRecoilValue(isCartOpen);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const isLogged = useRecoilValue(userIsLoggedIn);
  const serverCart = NewUseUpdatedCart();
  const userRole = useRecoilValue(userRoleAtom);
  const cartData = serverCart?.data;
  const ordersWithLocation = (cartData?.orders as any[]) ?? [];
  const cartOrder = ordersWithLocation.find(order => order?.locationId === selectedLocationId);
  const subTotal = cartOrder?.totalAmount || 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentOrder = ordersWithLocation.find(order => order?.locationId === selectedLocationId);
  const { invalidateCart } = useCartInvalidation();

  // Dedupe by location so each location entry is treated separately
  const uniqueOrders = useMemo(() => {
    const orders = ordersWithLocation;
    console.log(
      '🔍 Raw orders from server:',
      orders.map(o => ({ id: o.id, vendorId: o.vendorId, locationId: o.locationId, vendorName: o.vendorName }))
    );
    const byLocation = new Map<number, any>();
    for (const order of orders) {
      const key = order?.locationId ?? order?.id;
      if (key === undefined) continue;
      if (!byLocation.has(key)) {
        byLocation.set(key, order);
      }
    }
    const result = Array.from(byLocation.values());
    console.log(
      '✅ Unique orders after dedupe:',
      result.map(o => ({ id: o.id, vendorId: o.vendorId, locationId: o.locationId, vendorName: o.vendorName }))
    );
    return result;
  }, [ordersWithLocation]);

  // Reset selectedVendorId when modal reopens
  useEffect(() => {
    if (isModalOpen.onCartOpen) {
      setSelectedLocationId(null);
    }
  }, [isModalOpen.onCartOpen]);

  useEffect(() => {
    if (!currentOrder) {
      setSelectedLocationId(null);
    }
  }, [currentOrder]);

  const mutation = useMutation({
    mutationFn: clearCart,
    onSuccess: () => {
      enqueueSnackbar('Cart Cleared Successfully', { variant: 'success' });
      invalidateCart();
      setShowClearConfirm(false);
    },
    onError: () => {
      enqueueSnackbar('An Error Occurred', { variant: 'error' });
      setShowClearConfirm(false);
    }
  });

  const { mutateAsync: removeOrder, isLoading: deletingOrder } = useMutation<
    any,
    unknown,
    { userRole: string; orderId: number; locationId?: number }
  >({
    mutationFn: ({ userRole, orderId, locationId = 1 }) => {
      console.log('🗑️ DELETE REQUEST - OrderID:', orderId, 'CustomerType:', userRole, 'LocationID:', locationId);
      return deleteOrder(userRole, orderId, locationId);
    },
    onMutate: async ({ orderId, locationId = 1 }) => {
      setDeletingOrderId(`${orderId}-${locationId}`);
      // Cancel any outgoing refetches
      await queryClient.cancelQueries([QUERY_KEYS.CART, userRole]);

      // Snapshot the previous cart state
      const previousCart = queryClient.getQueryData([QUERY_KEYS.CART, userRole]);
      console.log('📦 Cart before delete:', (previousCart as any)?.orders?.map((o: any) => ({ id: o.id, vendorName: o.vendorName })));

      // Optimistically update the cart by removing the specific order
      queryClient.setQueryData([QUERY_KEYS.CART, userRole], (old: any) => {
        if (!old?.orders) return old;
        const updatedOrders = old.orders.filter((order: any) => {
          const keep = !(order.id === orderId && (order.locationId ?? 1) === locationId);
          console.log(
            `Order ${order.id} loc ${order.locationId} (${order.vendorName}): ${keep ? 'KEEP' : 'DELETE'}`
          );
          return keep;
        });
        console.log(
          '📦 Cart after delete:',
          updatedOrders.map((o: any) => ({ id: o.id, vendorName: o.vendorName, locationId: o.locationId }))
        );
        return {
          ...old,
          orders: updatedOrders
        };
      });

      return { previousCart };
    },
    onSuccess: (data) => {
      console.log('✅ Delete successful:', data);
      setDeletingOrderId(null);
      invalidateCart();
      setSelectedLocationId(null);
      enqueueSnackbar('Order removed successfully', { variant: 'success' });
    },
    onError: (err: any, variables, context: any) => {
      console.error('❌ Delete failed:', err?.response?.data || err.message);
      setDeletingOrderId(null);
      // Rollback to previous state on error
      if (context?.previousCart) {
        queryClient.setQueryData([QUERY_KEYS.CART, userRole], context.previousCart);
      }
      enqueueSnackbar('Failed to remove order', { variant: 'error' });
    }
  });

  const gotoCheckout = () => {
    if (!currentOrder) return;
    const locationId = currentOrder.locationId ?? 1;
    setIsModalOpen({ onCartOpen: false });
    navigate(`${CHECKOUT}/${locationId}`);
  };

  const handleDelete = () => {
    localStorage.removeItem(LOCALSTORAGE_KEYS.CART);
    setShowClearConfirm(false);
  };

  const handleCheckout = (locationId: number) => {
    console.log('🛒 Setting selected location ID:', locationId);
    setSelectedLocationId(locationId);
  };

  const handleBack = () => {
    console.log('⬅️ Going back, resetting selection');
    setSelectedLocationId(null);
  };

  const handleClearCart = () => {
    if (!isLogged) {
      handleDelete();
    } else {
      mutation.mutate();
    }
  };

  // Check if any product's quantity is lower than the minimumOrderQuantity
  const isCheckoutDisabled =
    userRole === 'BUSINESS' &&
    currentOrder?.products?.some((product: any) => product.quantity < product.minimumOrderQuantity);

  return (
    <Modal
      onModalClose={() => {
        setIsModalOpen({ onCartOpen: false });
      }}
      extraModalStyle={SideModalStyle}
      extraModalContentStyle={SideModalContentStyle}
      initial={initial}
      animate={animate}>
      <CartCon>
        {selectedLocationId === null && !currentOrder ? (
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl font-semibold">My Orders</h1>
            {ordersWithLocation.length > 0 && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="text-gray-500 text-md hover:text-red-600 transition-colors duration-200 flex items-center gap-2">
                {renderIcon(BiTrash, { className: 'text-red-600' })} Clear All
              </button>
            )}
          </div>
        ) : (
          <>
            {currentOrder && (
              <>
                <div className="flex gap-3 items-center mb-5">
                  {renderIcon(IoArrowBackCircle, {
                    className: 'text-3xl text-secondary cursor-pointer',
                    onClick: handleBack
                  })}
                  <h2 className="text-xl text-semibold text-[#444444]">Order Details</h2>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="relative w-10 h-10 rounded-full border overflow-hidden">
                    <Image
                      src={cartOrder?.vendorLogo || image}
                      alt="Vendor Logo"
                      className="object-cover w-full h-full"
                      onError={e => {
                        e.currentTarget.src = image;
                      }}
                    />
                  </div>

                  <div className="grid items-start">
                    <h2 className="text-[#1E223E] text-left text-lg font-medium">
                      {cartOrder?.vendorName}
                      {cartOrder?.vendorAddress?.city ? ` (${cartOrder?.vendorAddress?.city})` : ''}
                    </h2>
                    <h5 className="flex gap-2 items-center text-[#616161] text-sm">
                      Order {cartOrder?.orderRef} <span className="text-2xl">•</span> {cartOrder?.totalItems} item(s)
                    </h5>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {ordersWithLocation.length !== 0 ? (
          <>
            {selectedLocationId === null ? (
              <div className="border border-[#D3D3D3] mb-4 rounded-[10px] md:rounded-[20px] px-6 pb-4">
                {uniqueOrders.map((order: any) => (
                  <div key={order?.locationId ?? order?.id}>
                    <CartOrders
                      vendorName={order?.vendorName}
                      vendorLogo={order?.vendorLogo}
                      vendorCity={order?.vendorAddress?.city}
                      orderRef={order?.orderRef}
                      totalAmount={order?.totalAmount}
                      handleCheckout={() => handleCheckout(order?.locationId ?? order?.id)}
                      orderId={order?.locationId ?? order?.id}
                      deleteOrder={() => {
                        const customerType = userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR';
                        removeOrder({ userRole: customerType, orderId: order.id, locationId: order.locationId ?? 1 });
                      }}
                      totalItems={order?.totalItems}
                      deletingOrder={deletingOrderId === `${order.id}-${order.locationId ?? 1}`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {currentOrder && (
                  <div className="h-full relative">
                    {ordersWithLocation
                      .find(order => order?.locationId === selectedLocationId)
                      ?.products?.map((product: any, index: number) => (
                        <div
                          key={product?.productId}
                          className="flex justify-between py-4 border-b-[0.2px] border-[#D3D3D3]">
                          <CartProduct
                            item={product}
                            index={index}
                            vendorId={currentOrder?.vendorId ?? 0}
                            orderId={currentOrder?.id || 0}
                            customerType={userRole}
                          />
                        </div>
                      ))}

                    <CheckoutBottom>
                      <Divider />
                      <SubTotal>
                        <h2>Subtotal</h2>
                        <h4>{formatCurrency(subTotal)}</h4>
                      </SubTotal>
                      <Divider />
                      <Spacer height={50} />
                      <div className="flex w-full items-center gap-2">
                        <button
                          className={`text-white py-4 w-full rounded-md ${isCheckoutDisabled ? 'bg-secondary/50' : 'bg-secondary'
                            }`}
                          onClick={gotoCheckout}
                          disabled={isCheckoutDisabled}>
                          CHECKOUT
                        </button>
                        <button
                          className="text-red-600 bg-red-100 h-full aspect-square p-5 shadow-sm rounded-md"
                          onClick={() => {
                            if (currentOrder !== undefined) {
                              const customerType = userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR';
                              removeOrder({ userRole: customerType, orderId: currentOrder.id, locationId: currentOrder.locationId ?? 1 });
                            }
                          }}>
                          {deletingOrder || deletingOrderId === `${currentOrder.id}-${currentOrder.locationId ?? 1}` ? (
                            <Loader color="white" size="sm" />
                          ) : (
                            renderIcon(BiTrash)
                          )}
                        </button>
                      </div>
                    </CheckoutBottom>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <EmptyCon>
            <CartIcon />
            No Orders in Cart
          </EmptyCon>
        )}

        {/* Clear Cart Confirmation Modal */}
        {showClearConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Clear All Orders?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove all orders from your cart? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-200">
                  Cancel
                </button>
                <button
                  onClick={handleClearCart}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200">
                  {mutation.isLoading ? <Loader color="white" size="sm" /> : 'Clear All'}
                </button>
              </div>
            </div>
          </div>
        )}
      </CartCon>
    </Modal>
  );
};
